import React, { useMemo, useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
} from "react-admin";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  MenuItem,
  Popover,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { formatDate } from "../../utils/common";
import { getBillStatusChoices, getBillStatusColor, getBillStatusTitle } from "../../constants/BillStatus";
import { getBillTypeChoices, getBillTypeColor, getBillTypeTitle } from "../../constants/BillType";
import OnlineBillDetailDialog from "./online_bills_detail";
import {
  formatWaybillCityRoute,
  formatWaybillDriverText,
  getPayForWaybillFromRecord,
  getWaybillCargoName,
  getWaybillTruckPlate,
  getWaybillTruckPlateColorLabel,
  getWaybillTruckType,
} from "./pay_for";

type BillingRecord = {
  id: string | number;
  [key: string]: unknown;
};

const STATUS_ALL_SENTINEL = 999;

/** 11 位手机号（1 开头） */
const PHONE_PATTERN = /^1\d{10}$/;

const billStatusChoices = [
  { value: STATUS_ALL_SENTINEL, label: "全部状态" },
  ...getBillStatusChoices()
    .filter((item) => item.id !== 100)
    .map((item) => ({
      value: item.id,
      label: item.name,
    })),
];

const billTypeChoices = [
  { value: -1, label: "全部类型" },
  ...getBillTypeChoices()
    .filter((item) => item.id >= 0)
    .map((item) => ({
      value: item.id,
      label: item.name,
    })),
];

const getFirstDefined = (values: Array<unknown>): unknown => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

/**
 * 将未知值安全转为展示用字符串。
 * @param value 任意输入
 * @returns 字符串；无法转换时返回空串
 */
const parseStringValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

/**
 * 判断输入是否为手机号（否则按车牌号处理）。
 * @param keyword 车牌号或手机号
 */
const isPhoneKeyword = (keyword: string): boolean => PHONE_PATTERN.test(keyword);

/**
 * 从司机查询结果中取出用户 ID。
 * @param record 司机记录
 */
const getDriverIdFromRecord = (record: Record<string, unknown> | null | undefined): string => {
  if (!record) return "";
  return parseStringValue(getFirstDefined([record.id, record._id]));
};

const ListActions: React.FC = () => {
  const { filterValues, setFilters } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [draftType, setDraftType] = useState<number>(Number(filterValues.type));
  const [draftStatus, setDraftStatus] = useState<number>(filterValues.status != null ? Number(filterValues.status) : STATUS_ALL_SENTINEL);
  const [draftBillId, setDraftBillId] = useState<string>(parseStringValue(filterValues._id));
  const [draftDriverKeyword, setDraftDriverKeyword] = useState<string>("");
  const [searching, setSearching] = useState<boolean>(false);

  React.useEffect(() => {
    setDraftType(Number(filterValues.type));
    setDraftStatus(filterValues.status != null ? Number(filterValues.status) : STATUS_ALL_SENTINEL);
    setDraftBillId(parseStringValue(filterValues._id));
  }, [filterValues._id, filterValues.status, filterValues.type]);

  /**
   * 用车牌号或手机号查询司机，返回其 ID 作为收款方过滤条件。
   * @param keyword 车牌号或手机号
   */
  const resolvePayeeIdByDriverKeyword = async (keyword: string): Promise<string | null> => {
    const trimmed = keyword.trim();
    if (!trimmed) return null;

    const filter = isPhoneKeyword(trimmed)
      ? { phone: trimmed }
      : { "trucks.0.plate": trimmed };

    const resp = await dataProvider.getList("drivers", {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "DESC" },
      filter,
    });
    const first = Array.isArray(resp?.data) ? (resp.data[0] as Record<string, unknown> | undefined) : undefined;
    const driverId = getDriverIdFromRecord(first);
    return driverId || null;
  };

  const handleSearch = async () => {
    const nextFilters = {
      ...filterValues,
      type: draftType >= 0 ? draftType : undefined,
      status: draftStatus !== STATUS_ALL_SENTINEL ? draftStatus : undefined,
      _id: draftBillId.trim() || undefined,
    } as Record<string, unknown>;

    const keyword = draftDriverKeyword.trim();
    if (!keyword) {
      delete nextFilters.payee;
      setFilters(nextFilters, undefined);
      return;
    }

    try {
      setSearching(true);
      const payeeId = await resolvePayeeIdByDriverKeyword(keyword);
      if (!payeeId) {
        delete nextFilters.payee;
        setFilters(nextFilters, undefined);
        notify("未找到匹配的司机，请检查车牌号或手机号", { type: "warning" });
        return;
      }
      nextFilters.payee = payeeId;
      setFilters(nextFilters, undefined);
    } catch {
      notify("查询司机失败，请稍后重试", { type: "error" });
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => {
    setDraftType(-1);
    setDraftStatus(STATUS_ALL_SENTINEL);
    setDraftBillId("");
    setDraftDriverKeyword("");
    const nextFilters = { ...filterValues } as Record<string, unknown>;
    delete nextFilters.type;
    delete nextFilters.status;
    delete nextFilters._id;
    delete nextFilters.payee;
    setFilters(nextFilters, undefined);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void handleSearch();
    }
  };

  return (
    <TopToolbar sx={{ width: "100%", alignItems: "center", gap: 1, flexWrap: "wrap", px: 2, py: 1 }}>
      <MuiTextField
        select
        size="small"
        label="账单类型"
        value={draftType}
        onChange={(event) => setDraftType(Number(event.target.value))}
        sx={{ minWidth: 180 }}
      >
        {billTypeChoices.map((item) => (
          <MenuItem key={item.value || "all"} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </MuiTextField>
      <MuiTextField
        select
        size="small"
        label="状态"
        value={draftStatus}
        onChange={(event) => setDraftStatus(Number(event.target.value))}
        sx={{ minWidth: 180 }}
      >
        {billStatusChoices.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </MuiTextField>
      <MuiTextField
        size="small"
        label="账单ID"
        value={draftBillId}
        onChange={(event) => setDraftBillId(event.target.value)}
        onKeyDown={handleSearchKeyDown}
        sx={{ minWidth: 240 }}
      />
      <MuiTextField
        size="small"
        label="车牌号/手机号"
        value={draftDriverKeyword}
        onChange={(event) => setDraftDriverKeyword(event.target.value)}
        onKeyDown={handleSearchKeyDown}
        placeholder="查收款方司机"
        sx={{ minWidth: 200 }}
      />
      <Button variant="contained" onClick={() => void handleSearch()} disabled={searching}>
        {searching ? "查询中..." : "查询"}
      </Button>
      <Button onClick={handleReset} disabled={searching}>重置</Button>
    </TopToolbar>
  );
};

const parseNumberValue = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const formatFenToYuan = (value: unknown): string => {
  const fen = parseNumberValue(value);
  if (fen === null) return "-";
  const yuan = fen / 100;
  const normalized = Math.abs(yuan) < 1e-9 ? 0 : yuan;
  return `${normalized.toFixed(2)} 元`;
};

type UserRecord = Record<string, unknown> & { id?: string; _id?: string };

const parseUserRecord = (raw: unknown): UserRecord | null => {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) {
    const first = raw[0];
    return parseUserRecord(first);
  }
  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if (record.data !== undefined) {
      return parseUserRecord(record.data);
    }
    return record as UserRecord;
  }
  return null;
};

const getUserDisplayName = (record: UserRecord | null): string => {
  if (!record) return "";
  const name = record.name ?? record.username ?? record.realname ?? record.nickname;
  return parseStringValue(name);
};

const getUserDisplayAvatar = (record: UserRecord | null): string => {
  if (!record) return "";
  const avatar = record.avatar ?? record.photo ?? record.headimgurl;
  return parseStringValue(avatar);
};

const UsersContext = React.createContext<Record<string, UserRecord> | null>(null);

const normalizeUserId = (raw: unknown): string => parseStringValue(raw);

const getUserIdFromRecord = (record: UserRecord): string => {
  const id = record.id ?? record._id;
  return parseStringValue(id);
};

const UserInlineCell: React.FC<{ userId: string }> = ({ userId }) => {
  const users = React.useContext(UsersContext);
  const normalizedId = useMemo(() => normalizeUserId(userId), [userId]);
  const user = users && normalizedId ? users[normalizedId] : null;
  const avatar = getUserDisplayAvatar(user);
  const name = getUserDisplayName(user);
  
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 260 }}>
      <Avatar src={avatar || undefined} alt={name || "用户"} sx={{ width: 28, height: 28 }} />
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.2, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name || "-"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {normalizedId || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

const BillsDatagrid: React.FC = () => {
  const dataProvider = useDataProvider();
  const { data } = useListContext<BillingRecord>();
  const [usersById, setUsersById] = useState<Record<string, UserRecord>>({});
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [selectedBillId, setSelectedBillId] = useState<string>("");
  const [selectedBillRecord, setSelectedBillRecord] = useState<BillingRecord | null>(null);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    for (const record of Object.values(data ?? {})) {
      const payerId = normalizeUserId(getFirstDefined([(record as any).payer, (record as any).payer_id]));
      const payeeId = normalizeUserId(getFirstDefined([(record as any).payee, (record as any).payee_id]));
      if (payerId) ids.add(payerId);
      if (payeeId) ids.add(payeeId);
    }
    return Array.from(ids);
  }, [data]);

  React.useEffect(() => {
    if (userIds.length === 0) return;
    const missingIds = userIds.filter((id) => !usersById[id]);
    if (missingIds.length === 0) return;
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const resp = await dataProvider.getMany("users", { ids: missingIds });
        const records = Array.isArray(resp?.data) ? (resp.data as unknown[]) : [];
        const next = { ...usersById };
        for (const raw of records) {
          const record = parseUserRecord(raw);
          if (!record) continue;
          const id = getUserIdFromRecord(record);
          if (!id) continue;
          next[id] = record;
        }
        setUsersById(next);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [dataProvider, userIds, usersById]);

  const handleOpenDetail = (record: BillingRecord) => {
    const value = parseStringValue(getFirstDefined([(record as any).id, (record as any)._id]));
    if (!value) return;
    setSelectedBillId(value);
    setSelectedBillRecord(record);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedBillRecord(null);
  };

  return (
    <UsersContext.Provider value={usersById}>
      {usersLoading && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            正在加载本页用户信息...
          </Typography>
        </Box>
      )}
      <Datagrid
        rowClick={(_, __, record) => {
          handleOpenDetail(record as BillingRecord);
          return false as const;
        }}
        bulkActionButtons={false}
      >
        <FunctionField
          label="账单信息"
          render={(record: BillingRecord) => {
            const billId = parseStringValue(getFirstDefined([(record as any).id]));
            const title = parseStringValue(getFirstDefined([(record as any).title]));
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5, minWidth: 300 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    账单ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {billId || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    付款对象
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3, wordBreak: "break-all" }}
                  >
                    {title || "-"}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />
        <FunctionField
          label="关联运单"
          render={(record: BillingRecord) => {
            const waybill = getPayForWaybillFromRecord(record as Record<string, unknown>);
            if (!waybill) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            const cargoName = getWaybillCargoName(waybill);
            const route = formatWaybillCityRoute(waybill);
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5, minWidth: 200 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    wordBreak: "break-all",
                  }}
                  title={waybill.id}
                >
                  {waybill.id || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  货物：{cargoName || "-"}
                </Typography>
                {route && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    线路：{route}
                  </Typography>
                )}
              </Box>
            );
          }}
        />
        <FunctionField
          label="车辆司机"
          render={(record: BillingRecord) => {
            const waybill = getPayForWaybillFromRecord(record as Record<string, unknown>);
            if (!waybill) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            const driverText = formatWaybillDriverText(waybill);
            const plate = getWaybillTruckPlate(waybill);
            const truckType = getWaybillTruckType(waybill);
            const plateColor = getWaybillTruckPlateColorLabel(waybill);
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5, minWidth: 180 }}>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {driverText || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  车牌：{plate || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  车牌颜色：{plateColor || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  车长车型：{truckType || "-"}
                </Typography>
              </Box>
            );
          }}
        />
        <FunctionField
          label="收付款人"
          render={(record: BillingRecord) => {
            const payerId = normalizeUserId(getFirstDefined([(record as any).payer, (record as any).payer_id]));
            const payeeId = normalizeUserId(getFirstDefined([(record as any).payee, (record as any).payee_id]));
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5, minWidth: 320 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    付款方
                  </Typography>
                  <UserInlineCell userId={payerId} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    收款方
                  </Typography>
                  <UserInlineCell userId={payeeId} />
                </Box>
              </Box>
            );
          }}
        />
        <FunctionField
          label="金额"
          sortBy="amount"
          render={(record: BillingRecord) => {
            const amount = getFirstDefined([(record as any).amount, (record as any).total_amount, (record as any).money]);
            return formatFenToYuan(amount);
          }}
        />
        <FunctionField
          label="类型"
          sortBy="type"
          render={(record: BillingRecord) => {
            const typeRaw = getFirstDefined([(record as any).type, (record as any).bill_type]);
            const type = parseNumberValue(typeRaw);
            if (type === null) return <Typography variant="body2" color="text.secondary">-</Typography>;
            return (
              <Chip
                label={getBillTypeTitle(type)}
                size="small"
                sx={{
                  color: "#fff",
                  backgroundColor: getBillTypeColor(type),
                }}
              />
            );
          }}
        />
        <FunctionField
          label="状态"
          sortBy="status"
          render={(record: BillingRecord) => {
            const statusRaw = getFirstDefined([(record as any).status, (record as any).bill_status]);
            const status = parseNumberValue(statusRaw);
            if (status === null) return <Typography variant="body2" color="text.secondary">-</Typography>;
            return (
              <Chip
                label={getBillStatusTitle(status)}
                size="small"
                sx={{
                  color: "#fff",
                  backgroundColor: getBillStatusColor(status),
                }}
              />
            );
          }}
        />
        <TextField source="channel" label="渠道" />
        <FunctionField
          label="创建时间"
          source="createdAt"
          sortBy="createdAt"
          sortByOrder="DESC"
          render={(record: BillingRecord) => {
            const date = getFirstDefined([(record as any).createdAt, (record as any).created_at, (record as any).created]);
            return date ? formatDate(date as any) : "-";
          }}
        />
      </Datagrid>
      <OnlineBillDetailDialog
        open={detailOpen}
        billId={selectedBillId}
        initialRecord={selectedBillRecord}
        onClose={handleCloseDetail}
      />
    </UsersContext.Provider>
  );
};

const IdPopoverField: React.FC<{ record: BillingRecord; source: string; label: string }> = ({ record, source, label }) => {
  const rawValue = record ? (record as any)[source] : null;
  const value = parseStringValue(rawValue);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!value) return;
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const derivedFields = useMemo(() => {
    if (!record) return [];
    const candidates: Array<{ key: string; label: string; value: unknown }> = [
      { key: `${source}_name`, label: "名称", value: (record as any)[`${source}_name`] },
      { key: `${source}_type`, label: "类型", value: (record as any)[`${source}_type`] },
      { key: `${source}_phone`, label: "电话", value: (record as any)[`${source}_phone`] },
    ];
    return candidates
      .map((item) => ({ ...item, value: getFirstDefined([item.value]) }))
      .filter((item) => item.value !== null);
  }, [record, source]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      void 0;
    }
  };

  if (!value) return <Typography variant="body2" color="text.secondary">-</Typography>;

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        sx={{
          fontSize: 13,
          color: "primary.main",
          textDecoration: "underline",
          textUnderlineOffset: "2px",
          justifyContent: "flex-start",
        }}
      >
        {value}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 360 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-all" }}>
            {value}
          </Typography>
          {derivedFields.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {derivedFields.map((item) => (
                <Typography key={item.key} variant="caption" display="block" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  {item.label}：{parseStringValue(item.value)}
                </Typography>
              ))}
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <ButtonBase
              onClick={handleCopy}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "action.hover",
                fontSize: 12,
              }}
            >
              复制ID
            </ButtonBase>
            <ButtonBase
              onClick={handleClose}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "action.hover",
                fontSize: 12,
              }}
            >
              关闭
            </ButtonBase>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const ValuePopoverField: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!value) return;
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      void 0;
    }
  };
  if (!value) return <Typography variant="body2" color="text.secondary">-</Typography>;
  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        sx={{
          fontSize: 13,
          color: "primary.main",
          textDecoration: "underline",
          textUnderlineOffset: "2px",
          justifyContent: "flex-start",
        }}
      >
        {value}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 360 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, wordBreak: "break-all" }}>
            {value}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ButtonBase
              onClick={handleCopy}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "action.hover",
                fontSize: 12,
              }}
            >
              复制ID
            </ButtonBase>
            <ButtonBase
              onClick={handleClose}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "action.hover",
                fontSize: 12,
              }}
            >
              关闭
            </ButtonBase>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export const OnlineBillsList: React.FC = (props: any) => {
  return (
    <List {...props} actions={<ListActions />} sort={{ field: "createdAt", order: "DESC" }}>
      <BillsDatagrid />
    </List>
  );
};

export default OnlineBillsList;
